import { useCallback, useEffect, useState } from 'react';
import { useNotify, useRecordContext, type RaRecord } from 'react-admin';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { SectionCard, fmtCalendarDate, fmtDate } from '../../shared/Panel';
import { recordScoped, useStillMounted } from '../../shared/recordScoped';
import { toErrorMessage } from '../userOffers/orderActionsApi';
import { useFeature } from '../featureFlags/FeatureGate';
import {
  FEATURE_CARRIER_VERIFICATION,
  fetchCarrierVerification,
  saveCarrierVerification,
  type CarrierVerification,
  type VerificationItem,
  type VerificationItemView,
} from './carrierVerificationApi';

/**
 * Sekcja „Weryfikacja” w kartotece przewoźnika (24.10.2026, S-24.10-11,
 * przełącznik `carrierVerification`; paczka p. Mariusza z 21.10, BR-TRD-13:
 * „przewoźnik z niepełną weryfikacją może być przypisany jako wyjątek
 * z uzasadnieniem tradera”). Ubezpieczenie OCP, licencja / zezwolenie i VAT
 * czynny wpisuje Trade ręcznie — ani TMS, ani żadne API tych danych nie ma.
 * Stany i status zbiorczy liczy API (`carriers/carrier-verification.ts`);
 * front pokazuje to, co przyszło. Nieustalona to nie pełna.
 */

type LooseRecord = RaRecord & Record<string, unknown>;

/** Kolor czipu stanu pozycji — potwierdzone zielone, braki pomarańczowe, nie wpisano szare. */
const STATE_COLOR: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
  confirmed: 'success',
  missing: 'warning',
  expired: 'error',
  notEntered: 'default',
};

const STATUS_COLOR: Record<string, 'success' | 'warning' | 'default'> = {
  full: 'success',
  partial: 'warning',
  unknown: 'default',
};

/** Szczegóły pozycji jednym zdaniem: numer, termin, dzień sprawdzenia. */
export function itemDetails(i: VerificationItemView): string {
  if (i.present === null) return '—';
  if (i.item === 'vat') {
    return `${i.present ? 'czynny' : 'nieczynny'}${i.checkedOn ? `, sprawdzono ${fmtCalendarDate(i.checkedOn)}` : ''}`;
  }
  if (!i.present) return 'nie ma';
  return [
    i.number ? `nr ${i.number}` : null,
    i.validUntil ? `ważne do ${fmtCalendarDate(i.validUntil)}` : 'bez terminu',
  ]
    .filter((s): s is string => s !== null)
    .join(', ');
}

/** Data `RRRR-MM-DD` z odpowiedzi API (północ UTC) — do pola daty. */
const dayValue = (iso: string | null): string => (iso ? iso.slice(0, 10) : '');

const PanelBody = () => {
  const r = useRecordContext<LooseRecord>();
  const notify = useNotify();
  const stillMounted = useStillMounted();
  const [data, setData] = useState<CarrierVerification | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<VerificationItemView | null>(null);
  const [present, setPresent] = useState<'true' | 'false'>('true');
  const [number, setNumber] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [checkedOn, setCheckedOn] = useState('');
  const [busy, setBusy] = useState(false);
  const id = r?.id;

  const load = useCallback(async () => {
    if (id === undefined) return;
    try {
      const v = await fetchCarrierVerification(id);
      if (!stillMounted()) return;
      setData(v);
      setError(null);
    } catch (e) {
      if (stillMounted()) setError(toErrorMessage(e));
    }
  }, [id, stillMounted]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!r) return null;

  const start = (i: VerificationItemView) => {
    setEditing(i);
    setPresent(i.present === false ? 'false' : 'true');
    setNumber(i.number ?? '');
    setValidUntil(dayValue(i.validUntil));
    setCheckedOn(dayValue(i.checkedOn));
  };
  const item: VerificationItem | null = editing?.item ?? null;
  const ready =
    item === 'vat'
      ? checkedOn !== ''
      : item === 'ocp'
        ? present === 'false' || validUntil !== ''
        : item !== null;
  const save = async () => {
    if (!item) return;
    setBusy(true);
    try {
      const next = await saveCarrierVerification(r.id, item, {
        present: present === 'true',
        number: number.trim() || null,
        validUntil: validUntil || null,
        checkedOn: checkedOn || null,
      });
      notify('Zapisano weryfikację przewoźnika', { type: 'success' });
      setData(next);
      setEditing(null);
    } catch (e) {
      notify(toErrorMessage(e), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <SectionCard title="Weryfikacja">
      <Box data-testid="carrier-verification">
        {error ? <Alert severity="error">{error}</Alert> : null}
        {data ? (
          <>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
              <Typography variant="body2">Status weryfikacji:</Typography>
              <Chip
                size="small"
                color={STATUS_COLOR[data.status] ?? 'default'}
                label={data.statusLabel}
                data-testid="carrier-verification-status"
                data-status={data.status}
              />
            </Stack>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Pozycja</TableCell>
                  <TableCell>Stan</TableCell>
                  <TableCell>Szczegóły</TableCell>
                  <TableCell>Wpisał</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {data.items.map((i) => (
                  <TableRow key={i.item} data-testid={`carrier-verification-row-${i.item}`}>
                    <TableCell>{i.label}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={STATE_COLOR[i.state] ?? 'default'}
                        label={i.stateLabel}
                        data-testid={`carrier-verification-state-${i.item}`}
                        data-state={i.state}
                      />
                    </TableCell>
                    <TableCell>{itemDetails(i)}</TableCell>
                    <TableCell>{i.setBy ? `${i.setBy}, ${fmtDate(i.setAt)}` : '—'}</TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        onClick={() => start(i)}
                        data-testid={`carrier-verification-edit-${i.item}`}
                      >
                        {i.present === null ? 'Wpisz' : 'Zmień'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
              Wpisuje Trade po sprawdzeniu dokumentów przewoźnika — system niczego nie sprawdza sam. Pełna weryfikacja
              to trzy potwierdzone pozycje; „nieustalona” nie oznacza zgody. Akceptacja przewoźnika przy niepełnej albo
              nieustalonej weryfikacji wymaga uzasadnienia i zapisuje się w historii zlecenia. Każda zmiana idzie do
              dziennika działań.
            </Typography>
          </>
        ) : !error ? (
          <Typography variant="body2" color="text.secondary">
            Wczytywanie…
          </Typography>
        ) : null}
      </Box>
      <Dialog open={editing !== null} onClose={() => !busy && setEditing(null)} fullWidth maxWidth="xs">
        <DialogTitle>Weryfikacja: {editing?.label}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }} data-testid="carrier-verification-dialog">
            <TextField
              select
              size="small"
              label={item === 'vat' ? 'VAT czynny' : 'Dokument'}
              value={present}
              onChange={(e) => setPresent(e.target.value as 'true' | 'false')}
              slotProps={{ htmlInput: { 'data-testid': 'carrier-verification-present' } }}
            >
              <MenuItem value="true">{item === 'vat' ? 'tak' : 'jest'}</MenuItem>
              <MenuItem value="false">{item === 'vat' ? 'nie' : 'nie ma'}</MenuItem>
            </TextField>
            {item === 'license' && present === 'true' ? (
              <TextField
                size="small"
                label="Numer (opcjonalnie)"
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                slotProps={{ htmlInput: { maxLength: 100 } }}
              />
            ) : null}
            {item !== 'vat' && present === 'true' ? (
              <TextField
                size="small"
                type="date"
                label={item === 'ocp' ? 'Ważne do (wymagane)' : 'Ważne do (opcjonalnie)'}
                value={validUntil}
                onChange={(e) => setValidUntil(e.target.value)}
                slotProps={{ inputLabel: { shrink: true }, htmlInput: { 'data-testid': 'carrier-verification-valid-until' } }}
              />
            ) : null}
            {item === 'vat' ? (
              <TextField
                size="small"
                type="date"
                label="Sprawdzono dnia (wymagane)"
                value={checkedOn}
                onChange={(e) => setCheckedOn(e.target.value)}
                slotProps={{ inputLabel: { shrink: true }, htmlInput: { 'data-testid': 'carrier-verification-checked-on' } }}
              />
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)} disabled={busy}>
            Anuluj
          </Button>
          <Button variant="contained" onClick={() => void save()} disabled={busy || !ready} data-testid="dlg-ok">
            Zapisz
          </Button>
        </DialogActions>
      </Dialog>
    </SectionCard>
  );
};

const ScopedPanel = recordScoped(PanelBody);

/** Sekcja tylko z przełącznikiem `carrierVerification` — wyłączony: karta jak dotąd. */
export const CarrierVerificationSection = () => {
  const on = useFeature(FEATURE_CARRIER_VERIFICATION);
  return on ? <ScopedPanel /> : null;
};
